import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { cn } from "@/lib/utils";
import type { ModuleRegistryItem } from "@/modules/registry";

type ModuleLauncherProps = {
  modules: ModuleRegistryItem[];
  activeModule?: ModuleRegistryItem;
  className?: string;
};

/**
 * Переключение L2-модулей: tab «Модули» вплотную к верхнему краю хедера (только текст) + Sheet.
 * Нативный button — без rounded-md из ShadCN size=sm (иначе сверху остаётся зазор).
 */
export function ModuleLauncher({ modules, activeModule, className }: ModuleLauncherProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const selectModule = (route: string) => {
    const alreadyActive =
      Boolean(activeModule) &&
      (activeModule!.route === route || activeModule!.route.startsWith(`${route}/`));
    setOpen(false);
    if (alreadyActive) return;
    window.setTimeout(() => {
      navigate(route);
    }, 50);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          title="Открыть меню"
          className={cn(
            "m-0 box-border inline-flex h-5 items-center justify-center gap-1.5 border-0 px-3 py-0",
            "rounded-t-none rounded-b-md",
            "bg-foreground/80 text-[10px] font-semibold uppercase leading-none tracking-wider text-background/90",
            "hover:bg-foreground hover:text-background",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
            open && "bg-foreground text-background",
            className,
          )}
          aria-expanded={open}
          aria-label={open ? "Закрыть меню" : "Открыть меню"}
        >
          <Menu className="h-3 w-3 shrink-0" aria-hidden />
          МЕНЮ
        </button>
      </SheetTrigger>

      <SheetContent
        side="top"
        className="max-h-[min(85vh,560px)] overflow-y-auto border-b px-4 pb-6 pt-5 sm:px-6"
      >
        <SheetHeader className="space-y-1 pr-8 text-left">
          <div className="flex items-center gap-1.5">
            <SheetTitle className="text-base sm:text-lg">Модули</SheetTitle>
            <SectionHelpTooltip label="Справка по меню модулей" side="bottom">
              <p>
                Меню модулей платформы: переход между рабочими контурами. Кнопка «МЕНЮ» с иконкой
                бургера у верхнего края хедера открывает панель сверху.
              </p>
              <ul className="list-disc space-y-1 pl-4">
                <li>Выберите карточку — откроется workspace модуля.</li>
                <li>Активный модуль подсвечен в сетке.</li>
                <li>Закрытие: Esc, клик по затемнению или повторный клик по «МЕНЮ».</li>
              </ul>
            </SectionHelpTooltip>
          </div>
          <SheetDescription>
            Выберите рабочий модуль. В списке только включённые и доступные вам по правам.
          </SheetDescription>
        </SheetHeader>

        {modules.length === 0 ? (
          <p className="mt-6 text-sm text-muted-foreground">Нет доступных модулей.</p>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-3">
            {modules.map((module) => {
              const Icon = module.icon;
              const isActive =
                activeModule?.code === module.code ||
                Boolean(
                  activeModule &&
                    (activeModule.route === module.route ||
                      activeModule.route.startsWith(`${module.route}/`)),
                );
              return (
                <button
                  key={module.code}
                  type="button"
                  onClick={() => selectModule(module.route)}
                  className={cn(
                    "group flex flex-col gap-2 rounded-xl border bg-card p-4 text-left shadow-sm transition-colors",
                    "hover:border-primary/40 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isActive && "border-primary bg-primary/5 ring-1 ring-primary/30",
                  )}
                  aria-current={isActive ? "page" : undefined}
                >
                  <span
                    className={cn(
                      "inline-flex h-11 w-11 items-center justify-center rounded-lg bg-muted text-foreground",
                      "group-hover:bg-primary/10 group-hover:text-primary",
                      isActive && "bg-primary/15 text-primary",
                    )}
                  >
                    <Icon className="h-6 w-6" aria-hidden />
                  </span>
                  <span className="text-sm font-semibold leading-tight text-foreground">{module.label}</span>
                  <span className="text-xs leading-snug text-muted-foreground">{module.description}</span>
                </button>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default ModuleLauncher;
